import { NoteComposer } from '@/components/mentions/NoteComposer';
import React, { useEffect, useState, useCallback, useRef } from 'react';

import { Comments } from '@/components/runner/Comments';
import { useDispatch, useSelector } from 'react-redux';
import {
	createTeamConversationForCustomer,
	getTeamConversationForCustomer,
} from '@/actions/teams';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { ChevronRight, NotebookPen } from 'lucide-react';
import { RootState } from '@/store/store';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { useIsMobile } from '@/hooks/use-mobile';
import { DetailsSectionActions, DetailsSectionHeader } from './DetailsSection';

import {
	Dialog,
	DialogHeader,
	DialogContent,
	DialogTitle,
} from '@/components/ui/dialog';

interface CustomerNote {
	_id: string;
	messageCount: number;
	_customer?: string;
	error?: boolean;
}

interface ChatWindowProps {
	customerInfo: {
		_id: string;
		name: string;
		_business?: string;
	};
	options?: {
		isPipelineItem?: boolean;
		hideOuterBorder?: boolean;
	};
}

export const CustomerNotes: React.FC<ChatWindowProps> = ({
	customerInfo,
	options,
}) => {
	const [customerNotes, setCustomerNotes] = useState<CustomerNote | null>(
		null
	);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const isMobile = useIsMobile();
	const fetchingRef = useRef(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	const requestIdRef = useRef(0);

	// @ts-expect-error - this is a temporary fix to remove the type error
	const teams = useSelector((state: RootState) => state.teams);

	const dispatch = useDispatch();

	const fetchCustomerNotes = useCallback(async () => {
		if (fetchingRef.current) {
			abortControllerRef.current?.abort();
		}

		if (!customerInfo._id) {
			fetchingRef.current = false;
			return;
		}

		const controller = new AbortController();
		abortControllerRef.current = controller;
		fetchingRef.current = true;
		const requestId = ++requestIdRef.current;

		try {
			const response = (await dispatch(
				getTeamConversationForCustomer({
					_customer: customerInfo._id,
					signal: controller.signal,
				})
			)) as CustomerNote & { aborted?: boolean };

			// ignore if this request was superseded or aborted
			if (
				controller.signal.aborted ||
				requestId !== requestIdRef.current
			) {
				return;
			}

			if (!response.error && !response.aborted) {
				setCustomerNotes(response);
			} else {
				setCustomerNotes(null);
			}
		} catch {
			if (!controller.signal.aborted) {
				setCustomerNotes(null);
			}
		} finally {
			if (requestId === requestIdRef.current) {
				fetchingRef.current = false;
				abortControllerRef.current = null;
			}
		}
	}, [customerInfo._id, dispatch]);


	useEffect(() => {
		const foundConversation = teams.teamConversations.find(
			(conversation: CustomerNote) =>
				conversation._customer === customerInfo._id
		);
		if (foundConversation) {
			setCustomerNotes({
				_id: foundConversation._id,
				messageCount: foundConversation.teamMessages?.length || 0,
			});
		}
	}, [teams.teamConversations, customerInfo._id]);

	// Fetch notes when customer changes
	useEffect(() => {
		if (customerInfo._id) {
			// Reset the notes state when customer changes to avoid showing old data
			setCustomerNotes(null);
			fetchCustomerNotes();
		}
	}, [customerInfo._id, fetchCustomerNotes]);

	useEffect(() => {
		return () => {
			abortControllerRef.current?.abort();
		};
	}, []);

	const [isOpen, setIsOpen] = usePersistentOpen(
		`conversation-details:notes`,
	);

	// Only notepad members get new notes pushed live, so opening the notes
	// or the fullscreen view fetches them again.
	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open) fetchCustomerNotes();
	};

	const handleDialogOpenChange = (open: boolean) => {
		setIsDialogOpen(open);
		if (open) fetchCustomerNotes();
	};

	return (
		<>
			<div
				className={`overflow-clip shrink-0 ${
					options?.hideOuterBorder ? '' : 'border-b border-border/60'
				}`}
				key={customerInfo._id}
			>
				<Collapsible open={isOpen} onOpenChange={handleOpenChange}>
					<DetailsSectionHeader
						className="w-full"
						title={options?.isPipelineItem ? 'Customer Notes' : 'Notes'}
						actions={
							customerNotes && (
								<DetailsSectionActions className="max-md:hidden">
									<Button
										variant="link"
										size="sm"
										onClick={() => handleDialogOpenChange(true)}
										className="h-5 px-0 py-0 text-xs font-medium touch:relative touch:before:absolute touch:before:-inset-x-1 touch:before:-inset-y-3"
									>
										Fullscreen
									</Button>
								</DetailsSectionActions>
							)
						}
					/>
					<CollapsibleContent>
						<div className={customerNotes ? 'p-0' : 'px-4 pb-3'}>
							{customerNotes && isMobile ? (
								// Phones: the notes thread and its composer open full screen
								// rather than as a short scroller inside a scrolling page.
								<button
									type="button"
									onClick={() => handleDialogOpenChange(true)}
									className="flex min-h-12 w-full items-center gap-3 border-t border-border/50 px-4 py-2 text-left text-base text-foreground transition-colors active:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
								>
									<NotebookPen className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
									<span className="min-w-0 flex-1">Open notes</span>
									<ChevronRight className="size-5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
								</button>
							) : !customerNotes ? (
								<div className="space-y-2">
									<p className="text-xs text-muted-foreground">
										Internal notes about this customer,
										visible only to your team.
									</p>
									<NoteComposer key={customerInfo._id} draftKey={`first:${customerInfo._id}`}
                      _customer={customerInfo._id} _business={customerInfo._business!}
                      onSave={async (draft) => {
                        const result = await dispatch(createTeamConversationForCustomer({ _customer: customerInfo._id, _business: customerInfo._business, isRunner: true, title: customerInfo.name, initialMessage: draft.text, mentions: draft.mentions, requestId: draft.requestId }) as any) as any;
                        if (!result?.error) await fetchCustomerNotes();
                        return result;
                      }} />
								</div>
							) : (
								<Comments
                  onToggleCollapse={() => {}}
									className="min-h-[360px] h-[360px]"
									isCollapsed={false}
									infiniteScrollHeight="h-full"
									showHeader={false}
									isActivityOverview
									showAvatar={false}
								/>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>
			</div>
			{/* full screen dialog */}
			<Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
				<DialogContent
					mobileVariant="fullscreen"
					className="max-w-4xl w-[90vw] h-[90vh] max-xl:flex max-xl:flex-col md:max-xl:h-[calc(var(--app-height)-2rem)] max-md:gap-3 max-md:px-0 max-md:pb-0 max-md:kb-open:pb-0"
				>
					<DialogHeader className="max-md:px-4 max-md:pr-14">
						<DialogTitle className="text-lg font-semibold max-md:truncate">
							Customer Notes - {customerInfo.name}
						</DialogTitle>
					</DialogHeader>
					<div className="flex-1 overflow-hidden border rounded-xl max-xl:flex max-xl:min-h-0 max-xl:flex-col max-md:rounded-none max-md:border-x-0 max-md:border-b-0 max-md:pb-safe max-md:kb-open:pb-0">
						{customerNotes && (
							<Comments
                  onToggleCollapse={() => {}}
								isCollapsed={false}
								className="max-xl:h-full max-xl:min-h-0"
								infiniteScrollHeight="h-[calc(90vh-217px)] max-xl:h-full"
								showHeader={false}
								isActivityOverview
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};
